import { useState } from "react";
import PublicLayout from "@/components/PublicLayout";
import { Mail, MessageSquare, Send, CheckCircle2, Building2, Sparkles, Bug, CreditCard } from "lucide-react";

const TOPICS = [
  { key: "general", label: "General question", icon: MessageSquare },
  { key: "billing", label: "Billing & subscription", icon: CreditCard },
  { key: "bug", label: "Report a bug", icon: Bug },
  { key: "feature", label: "Feature request", icon: Sparkles },
  { key: "enterprise", label: "Enterprise / DPA", icon: Building2 },
];

export default function ContactPage() {
  const [topic, setTopic] = useState("general");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  // The form opens the user's mail client pre-populated. No backend round trip
  // — keeps things lightweight + GDPR-friendly (no inbox-side storage).
  const onSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`[RADAR] ${TOPICS.find((t) => t.key === topic)?.label || "Support"} — ${name || "User"}`);
    const body = encodeURIComponent(
      `Hi RADAR team,\n\n${message}\n\n— ${name || ""}${email ? "\n" + email : ""}\n\n(Sent from radaracctintel.com/contact)`
    );
    window.location.href = `mailto:support@radaracctintel.com?subject=${subject}&body=${body}`;
  };

  return (
    <PublicLayout>
      <section className="border-b border-[#1F1F1F]">
        <div className="max-w-5xl mx-auto px-6 md:px-8 py-16 md:py-20">
          <div className="kpi-label mb-3">/ CONTACT SUPPORT</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter text-white leading-[1.05] max-w-3xl">
            We're here to help.
          </h1>
          <p className="text-[#A3A3A3] mt-5 text-[15px] md:text-[16px] leading-relaxed max-w-2xl">
            Questions, bugs, billing issues, or enterprise enquiries — we read every message.
            Reach us directly or use the form below.
          </p>
          <a
            href="mailto:support@radaracctintel.com"
            data-testid="contact-direct-email"
            className="inline-flex items-center gap-2 mt-6 text-[15px] text-white hover:text-[#0A84FF] transition-colors group"
          >
            <Mail className="w-5 h-5 text-[#0A84FF]" strokeWidth={1.7}/>
            <span className="underline-offset-4 group-hover:underline">support@radaracctintel.com</span>
          </a>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-8 py-12 md:py-16">
        <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-6">
          {/* Topic selector */}
          <div>
            <label className="kpi-label block mb-3">/ WHAT'S THIS ABOUT?</label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {TOPICS.map((t) => {
                const Icon = t.icon;
                const active = topic === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    data-testid={`contact-topic-${t.key}`}
                    onClick={() => setTopic(t.key)}
                    className={`flex flex-col items-center gap-2 p-3 rounded-sm border transition-colors ${
                      active
                        ? "border-[#0A84FF] bg-[#0A84FF]/10 text-white"
                        : "border-[#262626] bg-[#141414] text-[#A3A3A3] hover:border-[#404040] hover:text-white"
                    }`}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.7}/>
                    <span className="text-[11px] font-mono-data tracking-wider uppercase text-center leading-tight">{t.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="kpi-label block mb-2" htmlFor="contact-name">/ NAME</label>
              <input
                id="contact-name"
                data-testid="contact-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full h-11 px-3 bg-[#0A0A0A] border border-[#262626] rounded-sm text-white placeholder:text-[#525252] focus:outline-none focus:ring-1 focus:ring-[#0A84FF] focus:border-[#0A84FF]"
              />
            </div>
            <div>
              <label className="kpi-label block mb-2" htmlFor="contact-email">/ WORK EMAIL</label>
              <input
                id="contact-email"
                data-testid="contact-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full h-11 px-3 bg-[#0A0A0A] border border-[#262626] rounded-sm text-white placeholder:text-[#525252] focus:outline-none focus:ring-1 focus:ring-[#0A84FF] focus:border-[#0A84FF]"
              />
            </div>
          </div>

          <div>
            <label className="kpi-label block mb-2" htmlFor="contact-message">/ MESSAGE</label>
            <textarea
              id="contact-message"
              data-testid="contact-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us what's going on…"
              rows={6}
              required
              className="w-full p-3 bg-[#0A0A0A] border border-[#262626] rounded-sm text-white placeholder:text-[#525252] focus:outline-none focus:ring-1 focus:ring-[#0A84FF] focus:border-[#0A84FF] resize-y"
            />
          </div>

          <div className="flex items-center justify-between gap-3 pt-2">
            <div className="flex items-center gap-2 text-[12px] text-[#525252]">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#32D74B]"/>
              Opens in your mail client — no data leaves your browser.
            </div>
            <button
              type="submit"
              data-testid="contact-submit"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-sm bg-[#0A84FF] hover:bg-[#409CFF] text-white font-medium tracking-tight transition-colors"
            >
              <Send className="w-4 h-4" strokeWidth={1.8}/>
              Send message
            </button>
          </div>
        </form>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-8 pb-16">
        <div className="border-t border-[#1F1F1F] pt-10">
          <div className="kpi-label mb-3">/ TYPICAL TURNAROUND</div>
          <p className="text-[14px] text-[#A3A3A3] leading-relaxed">
            We aim to reply within <span className="text-white font-mono-data">1 business day</span>.
            Billing issues, security questionnaires, and enterprise enquiries go directly to a human.
          </p>
        </div>
      </section>
    </PublicLayout>
  );
}
